import {
  type ForgotPasswordInput,
  type LoginInput,
  type LoginOutput,
  type ResetPasswordInput,
  type SecondFactorSendOutput,
  type SessionAccess,
  type SessionResponse,
  type StepUpOutput,
  sessionResponse,
} from '@fos/shared/contracts/identity';
import { ACCESS_LEVELS, type AccessLevel, MODULES } from '@fos/shared/enums';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch, NetworkError } from './api';

/** Sesja: GET /api/auth/session (docs/API_AUTH.md). Ustawia też ciasteczko CSRF. */
export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

export type ActiveSession = Extract<SessionResponse, { authenticated: true }>;

const AUTH = {
  session: '/api/auth/session',
  login: '/api/auth/login',
  logout: '/api/auth/logout',
  forgot: '/api/auth/password/forgot',
  reset: '/api/auth/password/reset',
  stepUp: '/api/auth/step-up',
  stepUpCode: '/api/auth/step-up/code',
  stepUpCodeVerify: '/api/auth/step-up/code/verify',
  secondFactorVerify: '/api/auth/second-factor/verify',
  secondFactorResend: '/api/auth/second-factor/resend',
  workspace: '/api/auth/workspace',
} as const;

/** Brakujące moduły w `access.modules` traktujemy jak „none” (bezpieczny domyślny). */
function normalizeAccess(access: unknown): unknown {
  if (!access || typeof access !== 'object') return access;
  const a = access as { modules?: Record<string, unknown> };
  const modules: Record<string, AccessLevel> = {};
  for (const m of MODULES) {
    const level = a.modules?.[m];
    modules[m] = (ACCESS_LEVELS as readonly unknown[]).includes(level) ? (level as AccessLevel) : 'none';
  }
  return { ...a, modules };
}

export function parseSession(raw: unknown): SessionResponse {
  const candidate =
    raw && typeof raw === 'object' && 'access' in raw ? { ...raw, access: normalizeAccess(raw.access) } : raw;
  const parsed = sessionResponse.safeParse(candidate);
  if (parsed.success) return parsed.data;
  throw new ApiError({ code: 'INTERNAL', status: 500, message: 'Nieprawidłowa odpowiedź serwera (sesja).' });
}

export async function fetchSession(): Promise<SessionResponse> {
  return parseSession(await apiFetch<unknown>(AUTH.session));
}

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => error instanceof NetworkError && failureCount < 2,
  });
}

/** Aktywna sesja (tylko wewnątrz chronionych tras, patrz app/guards.tsx). */
export function useActiveSession(): ActiveSession {
  const { data } = useSession();
  if (!data?.authenticated) throw new Error('useActiveSession poza chronioną trasą');
  return data;
}

/** Hasło. Wynik `second_factor_required` = sesja czeka na kod z maila (ekran „Wpisz kod z maila”). */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => apiFetch<LoginOutput>(AUTH.login, { method: 'POST', body: input }),
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
      // fetchQuery (a nie refetch): świeża sesja także wtedy, gdy nikt jej jeszcze nie obserwuje
      await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 0 });
    },
  });
}

/** Kod z maila przy logowaniu (POST /api/auth/second-factor/verify) → sesja `active`. */
export function useVerifySecondFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; trustDevice: boolean }) =>
      apiFetch<LoginOutput>(AUTH.secondFactorVerify, { method: 'POST', body: input }),
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
      await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 0 });
    },
  });
}

/** „Wyślij ponownie” przy logowaniu (limity: 30 s odstępu, 3 / 15 min). */
export function resendSecondFactor(): Promise<SecondFactorSendOutput> {
  return apiFetch<SecondFactorSendOutput>(AUTH.secondFactorResend, { method: 'POST' });
}

/** Kod potwierdzenia w zalogowanej sesji (podgląd sejfu, przełącznik 2FA, sesja po podniesieniu uprawnień). */
export function sendStepUpCode(): Promise<SecondFactorSendOutput> {
  return apiFetch<SecondFactorSendOutput>(AUTH.stepUpCode, { method: 'POST' });
}

export function confirmStepUpCode(code: string): Promise<StepUpOutput> {
  return apiFetch<StepUpOutput>(AUTH.stepUpCodeVerify, { method: 'POST', body: { code } });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>(AUTH.logout, { method: 'POST' }),
    onSettled: () => {
      // Ta sama instancja zapytania o sesję (obserwatorzy dostają zmianę), reszta danych znika z pamięci.
      queryClient.setQueryData<SessionResponse>(SESSION_QUERY_KEY, { authenticated: false });
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (input: ForgotPasswordInput) => apiFetch<{ ok: true }>(AUTH.forgot, { method: 'POST', body: input }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => apiFetch<{ ok: true }>(AUTH.reset, { method: 'POST', body: input }),
  });
}

export function confirmStepUp(password: string): Promise<StepUpOutput> {
  return apiFetch<StepUpOutput>(AUTH.stepUp, { method: 'POST', body: { password } });
}

export function useSwitchWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      apiFetch<{ ok: true }>(AUTH.workspace, { method: 'POST', body: { workspaceId } }),
    onSuccess: async () => {
      await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 0 });
      // dane poprzedniego workspace'u: reset i ponowne pobranie przez widoczne komponenty
      await queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
    },
  });
}

export type { SessionAccess };
